import * as React from 'react';
import * as InputTextTypes from './InputTextTypes';
import * as ValidateConstant from '../../wizard/constants/ValidateConstant';
import {isValidEmail, isValidNumber, isValidDecimalNumber} from '../utils/ValidationUtils';
import {
    formatDecimalNumber,
    keepDecimalCharacters,
    keepOnlyAlphaNumericCharacters,
    keepOnlyNumberCharacters,
    splitDecimal,
} from '../utils/NumberUtils';
import {InputTextProps, InputTextState} from '../types/components';
import AssetsManager from '../utils/AssetsManager';
import {currencyFormatter} from "../../wizard-motorfy/constants/ValidateConstant";
import WarningIcon from './WarningIcon';

class InputText extends React.Component<InputTextProps, InputTextState> {

    static defaultProps = {
        value: '',
        pattern: null,
        align: 'left',
        required: false,
        disabled: false,
        hasError: false,
        minValue: null,
        readOnly: false,
        acceptLeadingZeros: false,
    };

    constructor(props) {
        super(props);
        this.state = {
            isEditing: false
        };
    }

    onChange = (val) => {
        let value = val;
        value = this.maxLength(value);
        if (this.props.type === InputTextTypes.DECIMAL) {
            value = formatDecimalNumber(value, this.props.maxDecimals);
        }
        const error = this.validateEmpty(value) || this.minValidation(value) || this.validateType(value) ||
            this.validateMinValue(value);
        this.props.onChange(value, error, this.props.id);
    }

    onPressEnter = (e) => {
        if (e.keyCode === 13 && this.props.onPressEnter) {
            this.props.onPressEnter();
        }
    }

    minValidation = (value) => {
        const minLength = this.props.minLength;
        return minLength && value.length < minLength ? ValidateConstant.ERROR_MIN_LENGTH(minLength) : null;
    }

    maxLength = (val): string => {
        const {acceptLeadingZeros, maxLength, type} = this.props;
        let value = val;
        if (type === InputTextTypes.DECIMAL) {
            value = keepDecimalCharacters(value);
            const {integerPart, fractionalPart} = splitDecimal(value);
            if (fractionalPart) {
                if (integerPart.length > maxLength) {
                    // TODO tropicalize decimalSeparator
                    return `${String(integerPart).slice(0, maxLength)}.${fractionalPart}`;
                }
                return value;
            }
            if (value.substring(value.length - 1) === '.') {
                return value;
            }
        } else if (type === InputTextTypes.NUMBER) {
            value = keepOnlyNumberCharacters(value);
            if (!acceptLeadingZeros) {
                value = value.replace(/\b0+/g, '');
            }
        } else if (type === InputTextTypes.ALPHA_NUMERIC) {
            value = keepOnlyAlphaNumericCharacters(value);
        }
        if (String(value).trim().length > maxLength) {
            return value.slice(0, maxLength);
        }
        return value;
    }

    validateEmpty = (value) => {
        return this.props.required && value === '' ? ValidateConstant.ERROR_REQUIRED : null;
    }

    validateType = (value) => {
        switch (this.props.type) {
            case InputTextTypes.EMAIL:
                return isValidEmail(value) ? ValidateConstant.ERROR_EMAIL_FORMAT : null;
            case InputTextTypes.NUMBER:
                return isValidNumber(value) ? ValidateConstant.ERROR_NUMBER_FORMAT : null;
            case InputTextTypes.DECIMAL:
                return isValidDecimalNumber(value) ? null : ValidateConstant.ERROR_DECIMAL_NUMBER_FORMAT;
            default:
                return null;
        }
    }

    validateMinValue = (value) => {
        const minValue = this.props.minValue;
        return minValue != null && value <= minValue ? ValidateConstant.getMinValueAllowedErrorMessage(minValue) : null;
    }

    renderHelpOrErrorText = (hasError, labelError, invalidClass, helpText) => {

        if (hasError && labelError) {
            return <strong id={`${name}Help`}
                           className={`form-text ${invalidClass}`}>
                {labelError}
            </strong>;
        }
        if (helpText) {
            return helpText && (<strong id={`${name}Help`}
                                        className={'form-text'}>
                {helpText}
            </strong>);
        }
    }

    toggleEditing = (isEditing: boolean) => {
        if (!isEditing && this.props.value?.toString().endsWith('.')) {
            this.onChange(this.props.value.slice(0, -1));
        }
        this.setState({isEditing: isEditing});
    };

    handleAllowByPattern = (pattern) => (e) => {
        const deleteKeys = e.keyCode == 8 || e.keyCode == 46;
        const arrowKeys = e.keyCode >= 37 && e.keyCode <= 40;
        const isPasting = (e.ctrlKey || e.metaKey) && e.key === 'v';
        if (!deleteKeys && !arrowKeys && !isPasting && !e.key.match(pattern)) {
            e.preventDefault();
        }
    };

    handlePasteByPattern = (pattern) => (e) => {
        const pastedContent = e.clipboardData.getData('text');
        if (!pastedContent.match(pattern)) {
            e.preventDefault();
        }
    };

    toCurrency = (number) => currencyFormatter().format(number);

    render() {
        const {
            id,
            label,
            align,
            placeholder,
            name,
            value,
            pattern,
            required,
            disabled,
            hasError,
            labelError,
            minLength,
            type,
            autocomplete,
            helpText,
            inputCustomClass,
            readOnly,
            helpAction,
            showCurrencySymbol,
            showPercentSymbol,
        } = this.props;

        const invalidClass = hasError && labelError ? 'is-invalid' : '';
        const disabledClass = disabled ? 'text-secondary-lighten-2' : '';
        const inputClasses = [
            'form-control',
            `text-${align}`,
            invalidClass,
            disabledClass,
            inputCustomClass
        ].join(' ');

        const inputType = type === InputTextTypes.PASSWORD ? 'password' : 'text';
        const inputMode = type === InputTextTypes.NUMBER ? 'numeric' : 'text';
        const helpOrErrorText = this.renderHelpOrErrorText(hasError, labelError, invalidClass, helpText);
        return (
            <div className="form-group custom-input input-label">
                {label && <label htmlFor={id}>{label}{required && <span className="asterisk">*</span>}
                    {helpAction !== null && helpAction !== undefined &&
                    <img onClick={helpAction} className="ic cursor-pointer" alt="" height="12"
                         src={AssetsManager('/assets/images/common/questionIcon.png')}/>}
                    {hasError && labelError && <span className="ic"><WarningIcon/></span>}
                </label>}
                <input type={inputType}
                       className={inputClasses}
                       id={id}
                       required={required}
                       disabled={disabled}
                       placeholder={placeholder}
                       name={name}
                       value={
                           showCurrencySymbol && !this.state.isEditing && !!value
                               ? this.toCurrency(value)
                               : showPercentSymbol && !!value
                               ? `${value}%`
                               : value
                               ? value
                               : value === 0
                                   ? value
                                   : ''
                       }
                       minLength={minLength}
                       autoComplete={autocomplete}
                       onKeyDown={e => pattern ? this.handleAllowByPattern(pattern)(e) : this.onPressEnter(e)}
                       inputMode={inputMode}
                       readOnly={readOnly}
                       onChange={e => this.props.onChange ? this.onChange(e.target.value) : ''}
                       onBlur={() => this.toggleEditing(false)}
                       onFocus={() => this.toggleEditing(true)}
                       onPaste={e => {
                           if(pattern) {this.handlePasteByPattern(pattern)(e)}
                       }}
                       onClick={() => this.props.onClick ? this.props.onClick() : ''}/>
                {helpOrErrorText}
            </div>
        );
    }
}

export default InputText;
